import { useCallback, useRef, useState } from 'react';

const isTouchEvent = (e: Event): e is TouchEvent => {
  return 'touches' in e;
};

const preventDefault = (e: Event) => {
  if (!isTouchEvent(e)) return;

  if (e.touches.length < 2 && e.preventDefault) {
    e.preventDefault();
  }
};

interface Options {
  key?: string;
  delay?: number;
  shouldPreventDefault?: boolean;
}

const useLongPress = <T>(
  onLongPress: () => void,
  onClick?: () => void,
  { key = 'Enter', shouldPreventDefault = true, delay = 500 }: Options = {}
) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const target = useRef<EventTarget>();

  const start = useCallback(
    (event: React.MouseEvent<T> | React.KeyboardEvent<T> | React.TouchEvent<T>) => {
      if (shouldPreventDefault && event.target) {
        event.target.addEventListener('touchend', preventDefault, {
          passive: false
        });
        target.current = event.target;
      }
      timeout.current = setTimeout(() => {
        onLongPress();
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault]
  );

  const clear = useCallback(
    (shouldTriggerClick = true) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      if (shouldTriggerClick && !longPressTriggered) {
        onClick?.();
      }
      setLongPressTriggered(false);
      if (shouldPreventDefault && target.current) {
        target.current.removeEventListener('touchend', preventDefault);
      }
    },
    [shouldPreventDefault, onClick, longPressTriggered]
  );

  return {
    onMouseDown: (e: React.MouseEvent<T>) => start(e),
    onTouchStart: (e: React.TouchEvent<T>) => start(e),
    onKeyDown: (e: React.KeyboardEvent<T>) => {
      if (e.repeat || e.key !== key) return;
      start(e);
    },
    onMouseUp: () => clear(),
    onKeyUp: (e: React.KeyboardEvent<T>) => {
      if (e.repeat || e.key !== key) return;
      clear();
    },
    onMouseLeave: () => clear(false),
    onTouchEnd: () => clear()
  };
};

export default useLongPress;
